import { useEffect, useMemo, useState } from 'react';
import { Activity, AlertTriangle, ChevronDown, ChevronRight, Info, TriangleAlert } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { StatTile } from '@/components/ui/stat-tile';
import { cn } from '@/lib/utils';
import {
  type DeviceVerdict,
  type InterfaceSummary,
  useDeviceTrafficSummary,
} from '@/hooks/useDeviceTrafficSummary';
import { formatBits, formatTotal } from './format';
import { portName } from './rows';
import { PortRanking } from './PortRanking';
import { PortLedger } from './PortLedger';
import { RangeControl } from './RangeControl';
import { RANGE_MS, refreshMs, type TrafficRange } from './range';

/** Rows shown before the ledger's own disclosure, on a panel this size. */
const LEDGER_CAP = 10;

/**
 * Which ports on this device should someone be looking at.
 *
 * Collapsed on first open and expanded on demand. The verdict is hydrated
 * either way, because that is one sentence and it is the reason to expand or
 * not — but a reader walking a topology clicks a dozen nodes, and the ranking
 * and the ledger below are not worth building a dozen times to be glanced past.
 *
 * There is no throughput chart and no device total here. Summing 125 interfaces
 * double-counts every LAG's bits and every transiting frame, and "which port"
 * is the question this surface exists to answer anyway. A port's shape lives on
 * the port's own panel.
 */
export function DeviceTrafficSection({
  deviceId,
  range: controlledRange,
  onRangeChange,
  onSelectInterface,
}: {
  deviceId: string;
  range?: TrafficRange;
  onRangeChange?: (range: TrafficRange) => void;
  onSelectInterface?: (interfaceId: string) => void;
}) {
  const [localRange, setLocalRange] = useState<TrafficRange>('1h');
  const range = controlledRange ?? localRange;
  const setRange = onRangeChange ?? setLocalRange;
  const [expanded, setExpanded] = useState(false);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => { setNow(Date.now()); }, refreshMs(range));
    return () => { clearInterval(id); };
  }, [range]);
  const bounds = useMemo(() => ({ start: now - RANGE_MS[range], end: now }), [now, range]);

  const { data, isLoading, isError } = useDeviceTrafficSummary(deviceId, bounds);

  const select = onSelectInterface
    ? (row: InterfaceSummary) => { onSelectInterface(row.interfaceId); }
    : undefined;

  return (
    <section className="mt-4 rounded-md border border-border-subtle bg-muted/20 p-4">
      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <h4 className="text-sm font-semibold flex items-center gap-2">
          <Activity className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          Traffic
        </h4>
        <RangeControl value={range} onChange={setRange} />
      </header>

      {isLoading ? (
        <Skeleton className="h-16 w-full mt-3" />
      ) : isError || !data ? (
        <p className="mt-3 text-xs text-muted-foreground">
          The metric store did not answer. The device itself is unaffected.
        </p>
      ) : (
        <>
          <div className="mt-3">
            <DeviceVerdictStrip verdict={data.verdict} rows={data.interfaces} />
          </div>

          <button
            type="button"
            onClick={() => { setExpanded((previous) => !previous); }}
            aria-expanded={expanded}
            className="mt-3 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {expanded ? 'Hide the ports' : `Rank all ${String(data.verdict.portsTotal)} ports`}
          </button>

          {expanded ? (
            <div className="mt-4 space-y-5">
              <PortRanking rows={data.interfaces} totalRanked={data.verdict.portsRanked} onSelect={select} />
              <PortLedger rows={data.interfaces} cap={LEDGER_CAP} onSelect={select} />
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * The device variant of the verdict strip: which port, not what shape.
 *
 * Names the port in each tile rather than only the number, because "412" sends
 * nobody anywhere and "ge-0/0/24 · 412" is the whole answer.
 */
function DeviceVerdictStrip({ verdict, rows }: { verdict: DeviceVerdict; rows: InterfaceSummary[] }) {
  const ranked = rows.filter((row) => !row.aggregated);
  const worstErrors = pick(ranked, (row) => (row.errorsIn ?? 0) + (row.errorsOut ?? 0));
  const worstDiscards = pick(ranked, (row) => (row.discardsIn ?? 0) + (row.discardsOut ?? 0));
  const busiest = pick(ranked, (row) => Math.max(row.bitsIn ?? 0, row.bitsOut ?? 0));

  return (
    <div>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Worst by errors"
          figures="proportional"
          value={worstErrors ? formatTotal(worstErrors.value) : '—'}
          detail={worstErrors ? portName(worstErrors.row) : 'no errors on any port'}
          tone={worstErrors ? 'critical' : 'neutral'}
          toneLabel="frames rejected"
        />
        <StatTile
          label="Worst by discards"
          figures="proportional"
          value={worstDiscards ? formatTotal(worstDiscards.value) : '—'}
          detail={worstDiscards ? portName(worstDiscards.row) : 'no discards on any port'}
          tone={worstDiscards ? 'warning' : 'neutral'}
          toneLabel="frames dropped"
        />
        <StatTile
          label="Ports flapping"
          figures="proportional"
          value={String(verdict.portsFlapping)}
          detail={`of ${String(verdict.portsRanked)} ranked${verdict.portsTotal !== verdict.portsRanked ? ` · ${String(verdict.portsTotal - verdict.portsRanked)} LAG members not ranked` : ''}`}
          tone={verdict.portsFlapping > 0 ? 'warning' : 'neutral'}
          toneLabel="links unstable"
        />
        <StatTile
          label="Busiest"
          figures="proportional"
          value={busiest ? formatBits(busiest.value) : '—'}
          detail={busiest ? portName(busiest.row) : 'no traffic reported'}
        />
      </div>

      <p className="mt-3 flex items-start gap-2 text-sm text-foreground">
        <VerdictIcon severity={verdict.severity} />
        <span>{verdict.sentence}</span>
      </p>

      {rows.some((row) => row.resetInRange) ? (
        <p className={cn('mt-1.5 text-xs text-muted-foreground')}>
          {rows.filter((row) => row.resetInRange).length} port
          {rows.filter((row) => row.resetInRange).length === 1 ? "'s counters" : "s' counters"} restarted inside this
          window, so their totals are absent rather than counted from a partial run.
        </p>
      ) : null}
    </div>
  );
}

function pick(
  rows: InterfaceSummary[],
  measure: (row: InterfaceSummary) => number,
): { row: InterfaceSummary; value: number } | null {
  let best: { row: InterfaceSummary; value: number } | null = null;
  for (const row of rows) {
    const value = measure(row);
    if (value > 0 && (best === null || value > best.value)) best = { row, value };
  }
  return best;
}

function VerdictIcon({ severity }: { severity: DeviceVerdict['severity'] }) {
  switch (severity) {
    case 'critical':
      return <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5 text-sev-1" aria-label="critical" />;
    case 'warning':
      return <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-sev-3" aria-label="warning" />;
    default:
      return <Info className="w-4 h-4 shrink-0 mt-0.5 text-muted-foreground" aria-label="note" />;
  }
}
